// @ts-check
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { createGlobalStyle, css } from 'styled-components';
import LazyHydrate from 'react-lazy-hydration';
import { Link, ButtonStyledButton } from '@codex/basics';
import { CustomIcon, convertMobileIcon } from '../../Icon';
import BackDrop from '../BackDrop';
import TypeList from './TypeList';
import NavigationHeading from './NavigationHeading';
import { hasNav } from '../helpers';

const NoBodyOverflow = createGlobalStyle`
  body {
    overflow: hidden;
  }
`;

const NavigationWrapper = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;
  bottom: 0;
  z-index: 1;
  overflow-y: auto;
  overflow-x: hidden;

  @media ${({ theme }) => theme.breakpoints.desktop} {
    display: none;
  }
`;

const NavigationMenu = styled.div`
  background-color: ${({ theme }) => theme.colors.header.nav.mobile.background};
  padding-bottom: 15px;
  position: relative;
  z-index: 4;
`;

const NavigationItem = styled.li`
  background: ${({ theme }) => theme.colors.shades[7]};
  margin-bottom: 4px;
  position: relative;
  animation-duration: 0.3s;

  @keyframes moveLeft {
    from {
      left: -100%;
    }
    to {
      left: 0;
    }
  }

  @keyframes moveRight {
    from {
      left: 100%;
    }
    to {
      left: 0;
    }
  }
`;

const MenuLink = styled(Link)`
  position: relative;
  background: ${({ theme }) => theme.colors.shades[7]};
  color: ${({ theme }) => theme.colors.header.nav.link.text};
  display: block;
  font-size: 14px;
  padding: 14px 20px;
  padding-left: ${({ hasIcon }) => hasIcon && css`
    ${({ theme }) => (theme.name === 'brightworld' ? '58px' : '50px')}
  `};

  &:hover {
    background: ${({ theme }) => theme.colors.shades[7]};
    text-decoration: none;
  }
`;

const LabelIcon = styled(CustomIcon)`
  position: absolute;
  height: ${({ theme }) => (theme.name === 'brightworld' ? '25px' : '20px')};
  width: ${({ theme }) => (theme.name === 'brightworld' ? '25px' : '20px')};
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.link.default};
`;

const MenuItemIcon = styled(CustomIcon)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: ${({ theme }) => (theme.name === 'brightworld' ? 'auto' : '28px')};
  width: ${({ theme }) => (theme.name === 'brightworld' ? 'auto' : '28px')};
  right: ${({ theme }) => (theme.name === 'brightworld' ? '10px' : '5px')};
  color: ${({ theme }) => theme.colors.header.nav.arrow};
`;

const NavigationList = styled.ul`
  list-style: none;
  margin: 0 10px;
  padding: 0;

  ${NavigationItem}:last-child {
    margin-bottom: 0;

    ${MenuLink} {
      background: ${({ theme }) => theme.colors.header.nav.mark.background};
      color: ${({ theme }) => theme.colors.header.nav.mark.color};

      ${LabelIcon} {
        color: inherit;
      }

      ${MenuItemIcon} {
        color: ${({ theme }) => theme.colors.shades[7]};
      }
    }
  }
`;

const SubmenuList = styled.ul`
  list-style: none;
  margin: 0 10px;
  padding: 0;
`;

const Separator = styled.li`
  height: 45px;
  margin-bottom: 4px;
`;

const BackButton = styled(ButtonStyledButton)`
  border: 0;
  text-align: left;
  position: relative;
  background: ${({ theme }) => theme.colors.shades[7]};
  color: ${({ theme }) => theme.colors.header.nav.link.text};
  padding: 15px 30px;

  ${MenuItemIcon} {
    left: ${({ theme }) => (theme.name === 'brightworld' ? '10px' : '5px')};
    transform: translateY(-50%) rotate(180deg);
    color: ${({ theme }) => theme.name === 'brightworld' && 'inherit'};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.shades[7]};
  }
`;

const MobileHeaderNavigation = (props) => {
  const [direction, setDirection] = useState('');

  const openSubNav = (subMenu) => {
    props.openSubNav(subMenu);
    setDirection('moveRight');
  };

  const goBack = () => {
    props.goBack();
    setDirection('moveLeft');
  };

  const FILTERS_LEVEL = 2;

  const renderNavigationItem = (items, indexType) => items.map((subMenu, i) => {
    const hasSubnav = indexType < FILTERS_LEVEL && hasNav(subMenu);
    console.log(subMenu.icon, subMenu.label);
    return subMenu === 'separator' ? (
      <Separator key={`${i}-${indexType}`} />
    ) : (
      <NavigationItem
        key={`${i}-${indexType}`}
        style={{ animationName: direction }}
      >
        {subMenu.label && (
        <MenuLink
          href={hasSubnav ? undefined : subMenu.link}
          onClick={hasSubnav ? () => openSubNav(subMenu) : null}
          hasIcon={subMenu.icon}
        >
          {subMenu.icon && (
          <LabelIcon
            icon={subMenu.icon}
            iconCDNMobile={convertMobileIcon(subMenu.icon)}
            size="large"
          />
          )}
          {subMenu.label}
          {hasSubnav && subMenu.icon !== 'Deals' && (
          <MenuItemIcon
            icon="ChevronRight"
            iconCDNMobile="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/ArrowRightPurpleBrightworld.svg"
            size="large"
          />
          )}
          {hasSubnav && subMenu.icon === 'Deals' && (
          <MenuItemIcon
            icon="ChevronRight"
            iconCDNMobile="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/ArrowRightWhiteBrightworld.svg"
            size="large"
          />
          )}
        </MenuLink>
        )}
        {indexType >= FILTERS_LEVEL && hasNav(subMenu) && (
        <TypeList items={subMenu.nav} />
        )}
      </NavigationItem>
    );
  });

  const currentSubmenu =
    props.navigationHistory[props.navigationHistory.length - 1];

  const prevSubmenu =
    props.navigationHistory[props.navigationHistory.length - 2];

  return (
    <NavigationWrapper style={{ top: props.positionTop }}>
      <NoBodyOverflow />
      <NavigationMenu>
        {currentSubmenu && (
        <BackButton onClick={goBack}>
          <MenuItemIcon
            icon="ChevronRight"
            iconCDNMobile="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/ArrowRightPurpleBrightworld.svg"
            size="large"
          />
          {prevSubmenu ? prevSubmenu.label : 'Main menu'}
        </BackButton>
        )}
        <NavigationHeading currentSubmenu={currentSubmenu} />
        {!currentSubmenu ? (
          <LazyHydrate whenVisible>
            <NavigationList>
              {renderNavigationItem(props.navigationData.catalog.nav, -1)}
              <Separator />
              {renderNavigationItem(props.navigationData.pages.nav, 0)}
            </NavigationList>
          </LazyHydrate>
        ) : (
          <LazyHydrate whenVisible>
            <SubmenuList>
              {renderNavigationItem(
                currentSubmenu.style === 'services'
                  ? currentSubmenu.nav.reduce((a, b) => a.nav.concat(b.nav))
                  : currentSubmenu.nav,
                props.navigationHistory.length
              )}
            </SubmenuList>
          </LazyHydrate>
        )}
      </NavigationMenu>
      <BackDrop />
    </NavigationWrapper>
  );
};

MobileHeaderNavigation.propTypes = {
  goBack: PropTypes.func.isRequired,
  positionTop: PropTypes.number.isRequired,
  navigationData: PropTypes.shape({
    catalog: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      )
    }),
    pages: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      )
    })
  }),
  navigationHistory: PropTypes.arrayOf(
    PropTypes.shape({
      style: PropTypes.string,
      label: PropTypes.string,
      nav: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      )
    })
  ),
  openSubNav: PropTypes.func.isRequired
};

MobileHeaderNavigation.defaultProps = {
  navigationData: undefined,
  navigationHistory: []
};

export default MobileHeaderNavigation;
